import React, { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../../services/api";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();

/**
 * ScopeFiltersPanel - A modal dialog for scope filtering
 * Filters are only applied when the user clicks "Apply Filters"
 */
const ScopeFiltersPanel = ({
  scopeFilters,
  onFiltersChange,
  onSaveFilters,
  filtersDirty,
  savingFilters,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    organizations: true,
    initiatives: true,
    capabilities: true,
    contexts: true,
  });
  const [sectionSearchTerms, setSectionSearchTerms] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Local state for pending filter changes (not applied until confirmed)
  const [pendingFilters, setPendingFilters] = useState({
    filterOrganizations: [],
    filterInitiatives: [],
    filterCapabilities: [],
    filterContexts: [],
  });

  // Filter options
  const [organizations, setOrganizations] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [businessCapabilities, setBusinessCapabilities] = useState([]);
  const [businessContexts, setBusinessContexts] = useState([]);

  // Sync pending filters with actual filters when modal opens
  useEffect(() => {
    if (showModal) {
      setPendingFilters({
        filterOrganizations: scopeFilters.filterOrganizations || [],
        filterInitiatives: scopeFilters.filterInitiatives || [],
        filterCapabilities: scopeFilters.filterCapabilities || [],
        filterContexts: scopeFilters.filterContexts || [],
      });
    }
  }, [showModal, scopeFilters]);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showModal) {
        handleCancel();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showModal]);

  const loadFilterOptions = async () => {
    setLoadingOptions(true);
    try {
      const [orgsRes, initRes, capRes, ctxRes] = await Promise.all([
        apiFetch("/api/entities?type=organization&limit=500"),
        apiFetch("/api/entities?type=initiative&limit=500"),
        apiFetch("/api/entities?type=business-capability&limit=500"),
        apiFetch("/api/entities?type=business-context&limit=500"),
      ]);

      if (orgsRes.ok) {
        const data = await orgsRes.json();
        setOrganizations(data.entities || data || []);
      }
      if (initRes.ok) {
        const data = await initRes.json();
        setInitiatives(data.entities || data || []);
      }
      if (capRes.ok) {
        const data = await capRes.json();
        setBusinessCapabilities(data.entities || data || []);
      }
      if (ctxRes.ok) {
        const data = await ctxRes.json();
        setBusinessContexts(data.entities || data || []);
      }
    } catch (err) {
      console.error("Error loading filter options:", err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const toggleFilterSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleFilterItem = (filterKey, itemId) => {
    const currentFilters = pendingFilters[filterKey] || [];
    const updated = currentFilters.includes(itemId)
      ? currentFilters.filter((id) => id !== itemId)
      : [...currentFilters, itemId];
    setPendingFilters({ ...pendingFilters, [filterKey]: updated });
  };

  const selectAllItems = (filterKey, items) => {
    setPendingFilters({
      ...pendingFilters,
      [filterKey]: items.map((item) => item.id),
    });
  };

  const clearItems = (filterKey) => {
    setPendingFilters({ ...pendingFilters, [filterKey]: [] });
  };

  const clearAllPendingFilters = () => {
    setPendingFilters({
      filterOrganizations: [],
      filterInitiatives: [],
      filterCapabilities: [],
      filterContexts: [],
    });
  };

  const updateSectionSearch = (section, term) => {
    setSectionSearchTerms((prev) => ({ ...prev, [section]: term }));
  };

  // Apply filters and close modal
  const handleApply = () => {
    onFiltersChange(pendingFilters);
    setShowModal(false);
  };

  // Cancel and close modal (discard pending changes)
  const handleCancel = () => {
    setPendingFilters({
      filterOrganizations: scopeFilters.filterOrganizations || [],
      filterInitiatives: scopeFilters.filterInitiatives || [],
      filterCapabilities: scopeFilters.filterCapabilities || [],
      filterContexts: scopeFilters.filterContexts || [],
    });
    setShowModal(false);
  };

  // Count active filters (applied)
  const activeFilterCount =
    (scopeFilters.filterOrganizations?.length || 0) +
    (scopeFilters.filterInitiatives?.length || 0) +
    (scopeFilters.filterCapabilities?.length || 0) +
    (scopeFilters.filterContexts?.length || 0);

  // Count pending filters
  const pendingFilterCount =
    (pendingFilters.filterOrganizations?.length || 0) +
    (pendingFilters.filterInitiatives?.length || 0) +
    (pendingFilters.filterCapabilities?.length || 0) +
    (pendingFilters.filterContexts?.length || 0);

  // Check if pending differs from applied
  const hasPendingChanges =
    JSON.stringify(pendingFilters) !==
    JSON.stringify({
      filterOrganizations: scopeFilters.filterOrganizations || [],
      filterInitiatives: scopeFilters.filterInitiatives || [],
      filterCapabilities: scopeFilters.filterCapabilities || [],
      filterContexts: scopeFilters.filterContexts || [],
    });

  const hasAnyFilters =
    organizations.length > 0 ||
    initiatives.length > 0 ||
    businessCapabilities.length > 0 ||
    businessContexts.length > 0;

  const filterConfig = [
    {
      key: "filterOrganizations",
      label: "Organizations",
      icon: "🏢",
      items: organizations,
      section: "organizations",
    },
    {
      key: "filterInitiatives",
      label: "Initiatives",
      icon: "🎯",
      items: initiatives,
      section: "initiatives",
    },
    {
      key: "filterCapabilities",
      label: "Business Capabilities",
      icon: "⚙️",
      items: businessCapabilities,
      section: "capabilities",
    },
    {
      key: "filterContexts",
      label: "Business Contexts",
      icon: "🌐",
      items: businessContexts,
      section: "contexts",
    },
  ];

  // Get all selected items for the summary
  const pendingSelectedItems = useMemo(() => {
    const items = [];
    filterConfig.forEach(({ key, items: allItems, label }) => {
      const selectedIds = pendingFilters[key] || [];
      selectedIds.forEach((id) => {
        const item = allItems.find((i) => i.id === id);
        if (item) {
          items.push({ ...item, filterKey: key, category: label });
        }
      });
    });
    return items;
  }, [
    pendingFilters,
    organizations,
    initiatives,
    businessCapabilities,
    businessContexts,
  ]);

  // Helper to highlight search matches
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;
    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-500/40 text-white rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Group items alphabetically
  const groupByLetter = (items) => {
    const groups = {};
    items.forEach((item) => {
      const letter = (item.name || "?")[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(item);
    });
    return groups;
  };

  // Render a filter section
  const renderFilterSection = ({ key, label, icon, items, section }) => {
    const searchTerm = sectionSearchTerms[section] || "";
    const selectedIds = pendingFilters[key] || [];
    const isExpanded = expandedSections[section];

    // Filter items by search
    const filteredItems = items.filter(
      (item) =>
        !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Separate selected and unselected
    const selectedItems = filteredItems.filter((item) =>
      selectedIds.includes(item.id)
    );
    const unselectedItems = filteredItems.filter(
      (item) => !selectedIds.includes(item.id)
    );

    // Group unselected by letter if more than 15 items
    const shouldGroupByLetter = unselectedItems.length > 15;
    const groupedUnselected = shouldGroupByLetter
      ? groupByLetter(unselectedItems)
      : null;

    if (items.length === 0) return null;

    return (
      <div
        key={key}
        className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden"
      >
        <button
          onClick={() => toggleFilterSection(section)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">{icon}</span>
            <span className="text-base font-medium text-white">{label}</span>
            {selectedIds.length > 0 && (
              <span className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded-full font-semibold">
                {selectedIds.length} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">
              {items.length} available
            </span>
            <span
              className={`text-gray-400 transition-transform duration-200 ${
                isExpanded ? "rotate-180" : ""
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </span>
          </div>
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-700 pt-3">
            {/* Section search */}
            <div className="relative">
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => updateSectionSearch(section, e.target.value)}
                className="w-full px-4 py-2.5 pl-10 bg-gray-900 text-white rounded-lg border border-gray-600 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <svg
                className="w-4 h-4 absolute left-3 top-3 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchTerm && (
                <button
                  onClick={() => updateSectionSearch(section, "")}
                  className="absolute right-3 top-3 text-gray-400 hover:text-white"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Quick actions */}
            <div className="flex items-center justify-between">
              <div className="flex gap-4">
                <button
                  onClick={() => selectAllItems(key, filteredItems)}
                  className="text-sm text-blue-400 hover:text-blue-300 font-medium"
                >
                  Select {searchTerm ? "matching" : "all"} (
                  {filteredItems.length})
                </button>
                <button
                  onClick={() => clearItems(key)}
                  disabled={selectedIds.length === 0}
                  className={`text-sm font-medium ${
                    selectedIds.length > 0
                      ? "text-gray-400 hover:text-gray-300"
                      : "text-gray-600 cursor-not-allowed"
                  }`}
                >
                  Clear selection
                </button>
              </div>
              {searchTerm && filteredItems.length !== items.length && (
                <span className="text-sm text-gray-400">
                  Showing {filteredItems.length} of {items.length}
                </span>
              )}
            </div>

            {/* Items list */}
            <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-700 bg-gray-900/50">
              {/* Selected items first */}
              {selectedItems.length > 0 && (
                <div className="border-b border-gray-700">
                  <div className="sticky top-0 px-3 py-2 bg-blue-900/40 text-xs font-semibold text-blue-300 uppercase tracking-wider">
                    Selected ({selectedItems.length})
                  </div>
                  <div className="p-2 space-y-0.5">
                    {selectedItems.map((item) => (
                      <label
                        key={item.id}
                        className="flex items-center gap-3 text-sm text-white py-2 px-3 rounded-lg cursor-pointer hover:bg-blue-900/30 bg-blue-900/20"
                      >
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() => toggleFilterItem(key, item.id)}
                          className="w-4 h-4 text-blue-600 bg-gray-800 border-blue-500 rounded focus:ring-blue-500"
                        />
                        <span className="truncate">
                          {highlightMatch(item.name, searchTerm)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Unselected items */}
              {unselectedItems.length > 0 && (
                <>
                  {selectedItems.length > 0 && (
                    <div className="sticky top-0 px-3 py-2 bg-gray-800 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-700">
                      Available ({unselectedItems.length})
                    </div>
                  )}
                  {shouldGroupByLetter ? (
                    // Grouped by letter
                    Object.keys(groupedUnselected)
                      .sort()
                      .map((letter) => (
                        <div key={letter}>
                          <div className="sticky top-0 px-3 py-1.5 bg-gray-800/90 text-xs font-bold text-gray-500 border-b border-gray-700/50">
                            {letter}
                          </div>
                          <div className="p-2 space-y-0.5">
                            {groupedUnselected[letter].map((item) => (
                              <label
                                key={item.id}
                                className="flex items-center gap-3 text-sm text-gray-300 py-2 px-3 rounded-lg cursor-pointer hover:bg-gray-700/50"
                              >
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() =>
                                    toggleFilterItem(key, item.id)
                                  }
                                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                />
                                <span className="truncate">
                                  {highlightMatch(item.name, searchTerm)}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))
                  ) : (
                    // Simple list
                    <div className="p-2 space-y-0.5">
                      {unselectedItems.map((item) => (
                        <label
                          key={item.id}
                          className="flex items-center gap-3 text-sm text-gray-300 py-2 px-3 rounded-lg cursor-pointer hover:bg-gray-700/50"
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => toggleFilterItem(key, item.id)}
                            className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                          />
                          <span className="truncate">
                            {highlightMatch(item.name, searchTerm)}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}

              {filteredItems.length === 0 && (
                <div className="p-6 text-center text-gray-400 text-sm">
                  No {label.toLowerCase()} match "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Trigger Button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowModal(true)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            activeFilterCount > 0
              ? "bg-blue-600 text-white"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
          }`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          Scope Filters
          {activeFilterCount > 0 && (
            <span className="bg-white text-blue-600 px-1.5 py-0.5 rounded-full text-xs font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>
        {filtersDirty && (
          <span className="text-xs text-amber-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
            </svg>
            Unsaved
          </span>
        )}
      </div>

      {/* Modal Dialog */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCancel}
          />

          {/* Modal Content */}
          <div className="relative w-full max-w-4xl max-h-[90vh] bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 flex flex-col mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Scope Filters
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Filter report results to entities connected to selected items
                </p>
              </div>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Selected items summary */}
            {pendingSelectedItems.length > 0 && (
              <div className="px-6 py-3 bg-gray-800/50 border-b border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">
                    {pendingFilterCount} filter
                    {pendingFilterCount !== 1 ? "s" : ""} selected
                  </span>
                  <button
                    onClick={clearAllPendingFilters}
                    className="text-sm text-red-400 hover:text-red-300 font-medium"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                  {pendingSelectedItems.map((item) => (
                    <span
                      key={`${item.filterKey}-${item.id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-600/30 text-blue-200 rounded-full text-sm border border-blue-500/30"
                    >
                      <span className="max-w-[180px] truncate">
                        {item.name}
                      </span>
                      <button
                        onClick={() =>
                          toggleFilterItem(item.filterKey, item.id)
                        }
                        className="hover:text-white ml-0.5"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Filter sections */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingOptions ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <svg
                    className="animate-spin w-8 h-8 mb-3"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="text-base">Loading filter options...</span>
                </div>
              ) : !hasAnyFilters ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <div className="text-5xl mb-4">🔍</div>
                  <p className="text-base text-center max-w-md">
                    No filter entities available. Create Organizations,
                    Initiatives, Business Capabilities, or Business Contexts to
                    enable scope filtering.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filterConfig.map((config) => renderFilterSection(config))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 bg-gray-800/50 rounded-b-2xl">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-400">
                  {hasPendingChanges ? (
                    <span className="text-yellow-400 flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      Changes not applied
                    </span>
                  ) : filtersDirty ? (
                    <span className="text-amber-400 flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                        />
                      </svg>
                      Filters not saved to report
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCancel}
                    className="px-5 py-2.5 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApply}
                    disabled={!hasPendingChanges}
                    className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                      hasPendingChanges
                        ? "text-white bg-blue-600 hover:bg-blue-500"
                        : "text-gray-500 bg-gray-700 cursor-not-allowed"
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      // Apply pending changes first if any, then save
                      if (hasPendingChanges) {
                        onFiltersChange(pendingFilters);
                      }
                      onSaveFilters();
                      setShowModal(false);
                    }}
                    disabled={
                      savingFilters || (!filtersDirty && !hasPendingChanges)
                    }
                    className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                      (filtersDirty || hasPendingChanges) && !savingFilters
                        ? "text-white bg-green-600 hover:bg-green-500"
                        : "text-gray-500 bg-gray-700 cursor-not-allowed"
                    }`}
                  >
                    {savingFilters ? (
                      <>
                        <svg
                          className="animate-spin w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        Apply & Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScopeFiltersPanel;
